import axios from "axios";
import config from "../config";
import { HTTP_HEADERS, CONTENT_TYPES } from "../constants/api";
import { getUserTimezone } from "../utils/timezoneUtils";
import {
  InterceptorFactory,
  AuthInterceptor,
  ErrorResponseInterceptor,
  TokenProvider,
  NotificationFunction,
  RefreshTokenFunction,
  LoginRedirectFunction,
  LogoutRedirectFunction,
  ClaimSessionExpiredFunction,
} from "./interceptors";
import { setClaimToken, clearClaimToken } from "./wizardStorage";

// Create axios instance with base configuration
const api = axios.create({
  baseURL: config.api.baseURL,
  timeout: config.api.timeout,
  headers: {
    [HTTP_HEADERS.CONTENT_TYPE]: CONTENT_TYPES.JSON,
  },
});

// Create interceptor instances using factory pattern
const authInterceptor: AuthInterceptor =
  InterceptorFactory.createAuthInterceptor();
const errorInterceptor: ErrorResponseInterceptor =
  InterceptorFactory.createErrorInterceptor();

// Let the error interceptor read the current Bearer so it can silently retry a
// claim-token request that raced the login-time auth provider swap. Read
// localStorage directly (mirrors AuthContext.getAccessTokenSilently) rather
// than the swappable token provider, which is exactly what is stale mid-race.
errorInterceptor.setAccessTokenProvider(async () => {
  const token = localStorage.getItem("access_token");
  if (!token || token === "null" || token === "undefined") {
    return null;
  }
  return token;
});

// Function to set the token provider
export const setTokenProvider = (provider: TokenProvider) => {
  authInterceptor.setTokenProvider(provider);
};

// Function to set the notification function
export const setNotificationFunction = (
  notificationFn: NotificationFunction
) => {
  errorInterceptor.setNotificationFunction(notificationFn);
};

// Function to set the refresh token function
export const setRefreshTokenFunction = (
  refreshTokenFn: RefreshTokenFunction
) => {
  errorInterceptor.setRefreshTokenFunction(refreshTokenFn);
};

// Function to set the login redirect function
export const setLoginRedirectFunction = (
  loginRedirectFn: LoginRedirectFunction
) => {
  errorInterceptor.setLoginRedirectFunction(loginRedirectFn);
};

// Function to set the logout redirect function
export const setLogoutRedirectFunction = (
  logoutRedirectFn: LogoutRedirectFunction
) => {
  errorInterceptor.setLogoutRedirectFunction(logoutRedirectFn);
};

export const setClaimSessionExpiredFunction = (
  expiredFn: ClaimSessionExpiredFunction
) => {
  errorInterceptor.setClaimSessionExpiredFunction(expiredFn);
};

// Function to get access token for non-axios requests (e.g., fetch)
// This ensures consistent authentication across all API calls
export const getAccessToken = async (): Promise<string | null> => {
  return authInterceptor.getAccessToken();
};

// Setup interceptors using utility classes
api.interceptors.request.use(
  (config) => authInterceptor.handleRequest(config),
  (error) => authInterceptor.handleRequestError(error)
);

api.interceptors.response.use(
  (response) => errorInterceptor.handleResponse(response),
  (error) => errorInterceptor.handleError(error)
);

// API endpoints
export const API_ENDPOINTS = {
  HEALTH: "/health",
  DRAWS: "/api/draws",
  DRAW_SESSION: "/api/draws/session",
  CREATE_DRAW: "/api/draws",
  SUBMIT_DRAW: (drawId: string) => `/api/draws/${drawId}/submit`,
  STATS: "/api/stats",
  CERTIFICATE: (drawId: string) => `/api/draws/${drawId}/certificate`,
  WINNERS: (drawId: string) => `/api/draws/${drawId}/winners`,
  UPLOAD_ENTRIES: (drawId: string) => `/api/draws/${drawId}/files/upload`,
  ENTRY_FILE: (drawId: string, fileName: string) =>
    `/api/draws/${drawId}/files/${fileName}/download`,
  ENTRY_FILE_DELETE: (drawId: string, fileName: string) =>
    `/api/draws/${drawId}/files/${fileName}`,
  ENTRY_METADATA: (drawId: string, storedFileName: string) =>
    `/api/draws/${drawId}/files/${storedFileName}/metadata`,
  ENTRY_FILES: (drawId: string) => `/api/draws/${drawId}/files`,
  ENTRY_SUMMARY: (drawId: string) => `/api/draws/${drawId}/files/summary`,
  RESULTS_CSV: (drawId: string) => `/api/draws/${drawId}/results.csv`,

  // Multipart upload endpoints
  MULTIPART_INITIATE: (drawId: string) =>
    `/api/draws/${drawId}/files/multipart/initiate`,
  MULTIPART_PART_URL: (
    drawId: string,
    storedFileName: string,
    partNumber: number
  ) =>
    `/api/draws/${drawId}/files/multipart/${storedFileName}/part/${partNumber}`,
  MULTIPART_COMPLETE: (drawId: string, storedFileName: string) =>
    `/api/draws/${drawId}/files/multipart/${storedFileName}/complete`,
  MULTIPART_ABORT: (drawId: string, storedFileName: string) =>
    `/api/draws/${drawId}/files/multipart/${storedFileName}/abort`,
  CLAIM_DRAW: (drawId: string) => `/api/draws/${drawId}/claim`,
};

// Types
export interface CreateDrawRequest {
  draw_name: string;
  organisation: string;
  user_tz?: string;  // Optional: User's IANA timezone from browser
  turnstile_token?: string;
}

export interface ScrutineerSettings {
  scrutineer_required: boolean;
  scrutineer_name?: string;
  scrutineer_email?: string;
  scrutineer_job_title?: string;
  scrutineer_confirmation?: boolean;
}

export interface SubmitDrawRequest {
  draw_name: string;
  organisation: string;
  entry_method: "simple" | "standard" | "advanced";
  entry_count?: number;
  prizes: Array<{
    description: string;
    winners: number;
    reserves: number;
    estimated_value?: number;
  }>;
  csv_confirmation_setting?: {
    hasHeader?: boolean;
    ticketCountColumn?: string;
    participantIdentificationColumn?: string;  // Single column for winner identification
  };
  total_prize_pool_value?: number;
  scrutineer_settings?: ScrutineerSettings;
  max_prizes_per_entrant?: "one" | "multiple";
  scheduled_date_time?: string;
  status?: "draft" | "scheduled" | "completed";
  wizard_step?: number;
}

export interface DrawFileResponse {
  id: string;
  file_name: string; // stored filename
  original_filename: string;
  file_type: string;
  file_size: number;
  upload_date: string;
}

export interface PrizeResponse {
  id?: string;
  description: string;
  winners: number;
  reserves: number;
  display_order: number;
  estimated_value?: number;
  winner_results?: Array<{
    id: string;
    entry_number: number;
    prize_description: string;
    is_reserve: boolean;
    selection_order: number;
    participant_identification?: string;  // Winner identification from CSV (name, ID, or row number)
    certificate_generated: boolean;
    selected_at?: string;
  }>;
}

export interface DrawResponse {
  draw_id: string;
  draw_name: string;
  draw_no: string;
  organisation: string;
  status: string; // "draft" | "completed" | "scheduled" | "failed"
  entry_method?: string; // "simple" | "standard" | "advanced"
  entry_count?: number;
  csv_confirmation_setting?: any;
  prizes: PrizeResponse[];
  files: DrawFileResponse[];
  winners?: Array<{
    id: string;
    entry_number: number;
    prize_description: string;
    is_reserve: boolean;
    selection_order: number;
    participant_identification?: string;
    certificate_generated: boolean;
    selected_at?: string;
    winner_type: string;
  }>;
  created_at: string;
  updated_at?: string;
  created_by?: string;
  updated_by?: string;
  total_prize_pool_value?: number;
  scrutineer_settings?: ScrutineerSettings;
  max_prizes_per_entrant?: "one" | "multiple";
  scheduled_date_time?: string;
  completed_at?: string; // ISO timestamp when draw was completed
  execution_error?: string; // Error message for failed draws
  claim_token?: string;
  wizard_step?: number;
  payment_required?: boolean;
}

export interface Winner {
  entry_number: number;
  prize_description: string;
  is_reserve: boolean;
}

export interface DrawsResponse {
  draws: DrawResponse[];
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
  query?: string;
}

export interface ApiStats {
  total_draws: number;
  total_winners: number;
  api_version: string;
  status: string;
}

// File management types
export interface FileUploadResponse {
  file_name: string;
  url: string;
}

export interface FileInfoResponse {
  file_name: string;
  url: string;
}

export interface FileDeleteResponse {
  file_name: string;
  message: string;
}

export interface EntryMetadataResponse {
  originalFileName: string;
  storedFileName: string;
  size: number;
  mimeType: string;
  uploadedAt: string;
  status: "uploaded" | "parsed" | "failed";
  csvPreviewData: {
    rowCount: number;
    headerRowColumns: string[];
    entryRow1Columns: string[];
    entryRow2Columns: string[];
  };
  errorMessage?: string;
}

// UploadEntriesResponse is now the same as EntryMetadataResponse
export type UploadEntriesResponse = EntryMetadataResponse;

export interface Auth0Config {
  domain: string;
  client_id: string;
  audience?: string;
  login_url: string;
}

// Payment types
export interface PaymentIntentResponse {
  payment_id: string;
  client_secret: string;
  payment_intent_id: string;
  amount: number;
  currency: string;
  status: string;
}

export interface PaymentStatusResponse {
  payment_id: string;
  draw_id: string;
  status: string;
  amount: number;
  currency: string;
  created_at: string;
  confirmed_at?: string;
  last4?: string;
  card_brand?: string;
  funding?: string;
}

// API functions
export const apiService = {
  // Health check
  async checkHealth() {
    const response = await api.get(API_ENDPOINTS.HEALTH);
    return response.data;
  },

  // Check session (lightweight token/session validation - requires auth, no DB query)
  async checkSession() {
    const response = await api.get(API_ENDPOINTS.DRAW_SESSION);
    return response.data;
  },

  // Create a draft draw
  async createDraw(drawData: CreateDrawRequest): Promise<DrawResponse> {
    // Automatically inject user's timezone if not provided
    const requestData = {
      ...drawData,
      user_tz: drawData.user_tz || getUserTimezone()
    };
    const response = await api.post(API_ENDPOINTS.CREATE_DRAW, requestData);
    const draw = response.data as DrawResponse;
    if (
      config.features.anonymousDraftsEnabled &&
      draw.claim_token &&
      draw.draw_id
    ) {
      setClaimToken(draw.draw_id, draw.claim_token);
    }
    return draw;
  },

  async claimDraw(
    drawId: string,
    claimToken: string
  ): Promise<DrawResponse> {
    const response = await api.post(API_ENDPOINTS.CLAIM_DRAW(drawId), {
      claim_token: claimToken,
    });
    clearClaimToken(drawId);
    return response.data;
  },

  // Update an existing draft draw
  async updateDraw(
    drawId: string,
    drawData: SubmitDrawRequest,
    options?: { skipNotificationFor?: (err: any) => boolean }
  ): Promise<DrawResponse> {
    const skipNotificationFor = options?.skipNotificationFor;
    const response = await api.put(
      `${API_ENDPOINTS.DRAWS}/${drawId}`,
      drawData,
      skipNotificationFor ? ({ skipNotificationFor } as any) : undefined
    );
    return response.data;
  },

  // Submit/finalize an existing draft draw
  async submitDraw(
    drawId: string,
    drawData: SubmitDrawRequest
  ): Promise<DrawResponse> {
    const response = await api.post(
      API_ENDPOINTS.SUBMIT_DRAW(drawId),
      drawData
    );
    return response.data;
  },

  // Retry a failed draw
  async retryDraw(drawId: string): Promise<DrawResponse> {
    const response = await api.post(`${API_ENDPOINTS.DRAWS}/${drawId}/retry`);
    return response.data;
  },

  // Get all draws with optional filtering and pagination
  async getDraws(params?: {
    q?: string;
    status_filter?: "draft" | "scheduled" | "completed" | "failed";
    limit?: number;
    offset?: number;
  }): Promise<DrawsResponse> {
    const response = await api.get(API_ENDPOINTS.DRAWS, { params });
    return response.data;
  },

  // Get a single draw by ID
  async getDrawById(drawId: string): Promise<DrawResponse> {
    const response = await api.get(`${API_ENDPOINTS.DRAWS}/${drawId}`);
    return response.data;
  },

  // Get winners for a draw
  async getWinners(drawId: string): Promise<Winner[]> {
    const response = await api.get(API_ENDPOINTS.WINNERS(drawId));
    return response.data.winners;
  },

  // Download certificate PDF
  async downloadCertificate(drawId: string): Promise<Blob> {
    try {
      const response = await api.get(API_ENDPOINTS.CERTIFICATE(drawId), {
        responseType: "blob",
      });

      return response.data;
    } catch (error) {
      throw new Error(`Failed to download certificate: ${error}`);
    }
  },

  // Download entry file as blob
  async downloadEntryFile(drawId: string, fileName: string): Promise<Blob> {
    try {
      const response = await api.get(
        API_ENDPOINTS.ENTRY_FILE(drawId, fileName),
        {
          responseType: "blob",
        }
      );

      return response.data;
    } catch (error) {
      throw new Error(`Failed to download entry file: ${error}`);
    }
  },

  // Download results CSV
  async downloadResultsCsv(drawId: string): Promise<Blob> {
    try {
      const response = await api.get(API_ENDPOINTS.RESULTS_CSV(drawId), {
        responseType: "blob",
      });

      return response.data;
    } catch (error) {
      throw new Error(`Failed to download results CSV: ${error}`);
    }
  },

  // Delete a draw
  async deleteDraw(drawId: string) {
    const response = await api.delete(`${API_ENDPOINTS.DRAWS}/${drawId}`);
    return response.data;
  },

  // Get API statistics
  async getStats(): Promise<ApiStats> {
    const response = await api.get(API_ENDPOINTS.STATS);
    return response.data;
  },

  // Entry File Management APIs

  // Upload entries file for a draw (legacy multipart upload)
  async uploadEntryFile(
    drawId: string,
    file: File
  ): Promise<UploadEntriesResponse> {
    const formData = new FormData();
    formData.append("file", file);

    const response = await api.post(
      API_ENDPOINTS.UPLOAD_ENTRIES(drawId),
      formData,
      {
        headers: {
          [HTTP_HEADERS.CONTENT_TYPE]: CONTENT_TYPES.MULTIPART_FORM_DATA,
        },
      }
    );
    return response.data;
  },

  // Get entry file info with URL for preview/download
  async getEntryFile(
    drawId: string,
    fileName: string
  ): Promise<FileInfoResponse> {
    const response = await api.get(API_ENDPOINTS.ENTRY_FILE(drawId, fileName));
    return response.data;
  },

  // Get parsed metadata for uploaded entry file
  async getEntryMetadata(
    drawId: string,
    storedFileName: string
  ): Promise<EntryMetadataResponse> {
    const response = await api.get(
      API_ENDPOINTS.ENTRY_METADATA(drawId, storedFileName)
    );
    return response.data;
  },

  // Delete entry file by filename
  async deleteEntryFile(
    drawId: string,
    fileName: string
  ): Promise<FileDeleteResponse> {
    const response = await api.delete(
      API_ENDPOINTS.ENTRY_FILE_DELETE(drawId, fileName)
    );
    return response.data;
  },

  async multipartInitiate(
    drawId: string,
    params: {
      original_filename: string;
      content_type: string;
      file_size: number;
    }
  ): Promise<{ uploadId: string; storedFileName: string; [k: string]: any }> {
    const response = await api.post(
      API_ENDPOINTS.MULTIPART_INITIATE(drawId),
      undefined,
      {
        params: {
          original_filename: params.original_filename,
          content_type: params.content_type,
          file_size: String(params.file_size),
        },
      }
    );
    return response.data;
  },

  async multipartGetPartUrl(
    drawId: string,
    storedFileName: string,
    partNumber: number,
    uploadId: string
  ): Promise<{ presignedUrl: string; headers?: Record<string, string> }> {
    const response = await api.get(
      API_ENDPOINTS.MULTIPART_PART_URL(drawId, storedFileName, partNumber),
      { params: { upload_id: uploadId } }
    );
    return response.data;
  },

  async multipartComplete(
    drawId: string,
    storedFileName: string,
    body: { uploadId: string; parts: Array<{ partNumber: number; etag: string }> }
  ): Promise<{ s3Bucket: string; s3Key: string; [k: string]: any }> {
    try {
      const response = await api.post(
        API_ENDPOINTS.MULTIPART_COMPLETE(drawId, storedFileName),
        body
      );
      return response.data;
    } catch (error: any) {
      // Surface the backend's `detail` so Uppy shows it instead of a generic axios message.
      const detail =
        error?.response?.data?.detail || error?.response?.data?.message;
      if (detail) {
        throw new Error(detail);
      }
      throw error;
    }
  },

  async multipartAbort(
    drawId: string,
    storedFileName: string,
    uploadId: string
  ): Promise<void> {
    await api.delete(API_ENDPOINTS.MULTIPART_ABORT(drawId, storedFileName), {
      params: { upload_id: uploadId },
    });
  },

  // Get Auth0 configuration
  async getAuth0Config(): Promise<Auth0Config> {
    const response = await api.get("/auth/config");
    return response.data;
  },

  async getIntercomToken(): Promise<{
    intercom_user_jwt: string | null;
    app_id: string | null;
  }> {
    const response = await api.get("/api/user/intercom-token");
    return response.data;
  },

  // Randomness monitoring endpoints
  async getRandomnessQualityReport(days: number = 7) {
    const response = await api.get(
      `/api/randomness/quality-report?days=${days}`
    );
    return response.data;
  },

  async getRandomnessMonitoringStatus() {
    const response = await api.get("/api/randomness/monitoring-status");
    return response.data;
  },

  async collectRandomnessSample(sampleSize: number = 1000) {
    const response = await api.post(
      `/api/randomness/collect-sample?sample_size=${sampleSize}`
    );
    return response.data;
  },

  // Audit & Activity Feed endpoints
  async getAuditLogs(params?: {
    draw_id?: string;
    entity_type?: string;
    action_type?: string;  // Comma-separated for multiple types
    start_date?: string;
    end_date?: string;
    is_comment?: boolean;
    limit?: number;
    offset?: number;
  }) {
    const response = await api.get("/api/audit", { params });
    return response.data;
  },

  async getDrawAuditLogs(
    drawId: string,
    params?: {
      include_comments?: boolean;
      limit?: number;
      offset?: number;
    }
  ) {
    const response = await api.get(`/api/audit/draws/${drawId}`, { params });
    return response.data;
  },

  async getDrawActivityFeed(drawId: string, limit: number = 50) {
    const response = await api.get(`/api/audit/draws/${drawId}/activity`, {
      params: { limit },
    });
    return response.data;
  },

  async addDrawComment(drawId: string, commentText: string) {
    const response = await api.post(`/api/audit/draws/${drawId}/comments`, {
      comment_text: commentText,
    });
    return response.data;
  },

  async getRecentAuditLogs(limit: number = 100, offset: number = 0) {
    const response = await api.get("/api/audit/recent", {
      params: { limit, offset },
    });
    return response.data;
  },

  // Payment endpoints
  async createPaymentIntent(data: {
    draw_id: string;
  }): Promise<PaymentIntentResponse> {
    const response = await api.post(
      "/api/payments/create-payment-intent",
      data
    );
    return response.data;
  },

  async getPaymentStatus(
    paymentIntentId: string,
    refreshIfPending?: boolean
  ): Promise<PaymentStatusResponse> {
    const response = await api.get(`/api/payments/status/${paymentIntentId}`, {
      params: refreshIfPending ? { refresh_if_pending: true } : undefined,
    });
    return response.data;
  },
};

export default apiService;
